import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator';
import { State, Getter, Action } from 'vuex-class';
import { PropType } from 'vue';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { User } from '@/models/user';
import { Album } from '@/models/album';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { Photo } from '@/models/photo';
import { AlbumCreateRequest } from '@/services/albums/albums.interfaces';
// @ts-ignore no d.ts
import VirtualCollection from 'vue-virtual-collection';
import PremiumOverlay from '../premiumoverlay/premiumoverlay.component.vue';
import S3Image from '../s3-image/S3Image.vue';
import { Logger, Storage } from 'aws-amplify';
// @ts-ignore : legacy build pour webpack4/vue2
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
// @ts-ignore
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
const logger = new Logger('AlbumComponent');

type S3Item = {
  url: string;
  key: string;
  thumb?: string; // miniature PDF en base64
};

@Component({
  components: {
    S3Image,
    PremiumOverlay,
  },
})
export default class AlbumComponent extends Vue {
  @Ref('photos') public readonly photosRef!: HTMLDivElement;
  @Ref('scroller') public readonly scroller!: VirtualCollection;
  @Ref('createAlbumModal') public readonly createAlbumModal!: { show: () => void; hide: () => void };
  @Prop({ type: Object, required: true }) private readonly camera!: Camera;
  @Prop({ type: Object, required: true }) private readonly project!: Project;
  @Prop({ required: true }) readonly isPremium!: boolean;
  @Prop({ type: Object as PropType<Album | null>, default: null })
  readonly album!: Album | null;

  @State('loadingAlbum', { namespace: 'album' }) private readonly loadingAlbum!: boolean;
  @State('albums', { namespace: 'album' }) private readonly albums!: Album[];
  @State('user', { namespace: 'profile' }) private user!: User;

  @State('photos', { namespace: 'photo' }) private photos!: Photo[];
  @Getter('onlySlowmotion', { namespace: 'photo' }) private slowMotions!: Photo[];
  @Getter('projectWithId', { namespace: 'projects' }) private projectWithId!: (id: string) => Project | undefined;

  @Action('loadOneAlbum', { namespace: 'album' }) private loadOneAlbum!: (albumId: string) => Promise<Album>;
  @Action('loadAlbum', { namespace: 'album' }) private loadAlbum!: (payload: {
    projectId: string;
    cameraId: string;
  }) => Promise<Album[]>;
  @Action('createNewAlbum', { namespace: 'album' }) private readonly createNewAlbum!: (albumCreateRequest: {
    cameraId: string;
    projectId: string;
    name: string;
  }) => Promise<Album[]>;

  private visible = false;

  private scrollerWidth = 0;
  private scrollerColumns = 0;
  private scrollerImageWidth = 0;
  private items: { data: Album }[] = [];
  private newAlbumName = '';
  private s3Photos: Record<string, Array<S3Item>> = {};

  private createAlbumRequest: AlbumCreateRequest = new AlbumCreateRequest();

  public get isAdmin(): boolean {
    return (
      this.user !== undefined &&
      (this.project.owner.id === this.user.id || this.project.administratorId.includes(this.user.id))
    );
  }

  // --- Helpers de type de fichier
  private isVideoKey(key: string): boolean {
    return /\.(mp4|mov|m4v|webm)$/i.test(key);
  }
  private isImageKey(key: string): boolean {
    return /\.(jpe?g|png|webp|gif)$/i.test(key);
  }
  private isPdfKey(key: string): boolean {
    return /\.pdf$/i.test(key);
  }

  // --- Accès filtré aux médias S3 d'un album
  private getS3Items(albumId: string): Array<{ key: string; url: string }> {
    return this.s3Photos[albumId] || [];
  }
  private getS3Images(albumId: string) {
    return this.getS3Items(albumId).filter((it) => this.isImageKey(it.key));
  }
  private getS3Videos(albumId: string) {
    return this.getS3Items(albumId).filter((it) => this.isVideoKey(it.key));
  }
  private getS3Pdfs(albumId: string) {
    return this.getS3Items(albumId).filter((it) => this.isPdfKey(it.key));
  }

  // --- Compteurs (séparent bien images / vidéos / pdf)
  public countCameraImages(photos: Array<any>): number {
    // les "photos" du backend album n'auront pas de PDF ; on exclut juste les vidéos
    return photos.filter((p) => !this.isVideoKey(p.key || '')).length;
  }
  public countCameraVideos(photos: Array<any>): number {
    return photos.filter((p) => this.isVideoKey(p.key || '')).length;
  }
  public countS3Images(items: Array<{ key: string }>): number {
    return items.filter((it) => this.isImageKey(it.key)).length;
  }
  public countS3Videos(items: Array<{ key: string }>): number {
    return items.filter((it) => this.isVideoKey(it.key)).length;
  }
  public countS3Pdfs(items: Array<{ key: string }>): number {
    return items.filter((it) => this.isPdfKey(it.key)).length;
  }

  public async mounted(): Promise<void> {
    console.log('Album at mounted:', this.album); // Log l'état de album
    console.log('Camera at mounted:', this.camera);
    await new Promise((resolve) => setTimeout(resolve, 2000));

    await this.loadDataAlbum();
    console.log('s3Photos initialized:', this.s3Photos);

    if (this.albums.length > 0) {
      this.onAlbumsStoreChange(this.albums, []);
    }

    this.$nextTick(() => {
      window.addEventListener('resize', this.updatePhotosSize);
    });
  }

  public updatePhotosSize(): void {
    if (this.scroller) {
      const w = this.photosRef.clientWidth;
      this.scrollerColumns = Math.ceil(w / 150);
      this.scrollerWidth = w;
      this.scrollerImageWidth = (w - 10 - this.scrollerColumns * 5) / this.scrollerColumns;
      this.items = this.items.slice(0);
    }
  }

  private getS3Photos(albumId: string): Array<any> {
    return this.s3Photos[albumId] || [];
  }

  private async loadDataAlbum(): Promise<void> {
    console.log('loadDataAlbum called');
    await this.loadAlbum({ projectId: this.project.id, cameraId: this.camera.id });

    for (const album of this.albums) {
      await this.fetchS3Photos(album); // Assure un chargement séquentiel de chaque album
    }
  }

  private async generatePdfThumb(url: string, scale = 0.4): Promise<string | undefined> {
    try {
      const task = pdfjsLib.getDocument(url); // url signé S3 => OK CORS
      const pdf = await task.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: ctx, viewport }).promise;
      return canvas.toDataURL('image/png'); // miniature base64
    } catch (e) {
      console.error('PDF thumb error:', e);
      return undefined;
    }
  }

  public cellSizeAndPositionGetter(item: Photo, index: number): unknown {
    // compute size and position
    const w = this.scrollerImageWidth + 5;
    const h = this.scrollerImageWidth;
    return {
      width: w,
      height: h,
      x: (index % this.scrollerColumns) * w,
      y: Math.floor(index / this.scrollerColumns) * h,
    };
  }

  @Watch('album', { immediate: true })
  private async onAlbumChange(newAlbum: Album | null) {
    if (!newAlbum || !newAlbum.id) return;
    this.s3Photos[newAlbum.id] = [];
    await this.fetchS3Photos(newAlbum);
  }

  private async fetchS3Photos(album: Album): Promise<void> {
    if (!this.camera || !album) return;

    try {
      const folderPath = `${album.cameraId}/album/${album.name}/`;
      const data = await Storage.list(folderPath);

      const signed: S3Item[] = await Promise.all(
        data.map(async (item: { key: string }) => {
          const url = (await Storage.get(item.key, { expires: 3600 })) as string;
          return { url, key: item.key };
        })
      );

      // fabrique les miniatures PDF (en série pour éviter les pics CPU)
      for (const it of signed) {
        if (this.isPdfKey(it.key)) {
          it.thumb = await this.generatePdfThumb(it.url); // undefined si échec -> fallback icône
        }
      }

      this.s3Photos[album.id] = signed;
    } catch (err) {
      console.error(`Erreur S3 pour ${album.name}:`, err);
      this.s3Photos[album.id] = [];
    }
  }

  public countPhotos(photos: Array<any>): number {
    return photos.filter((photo) => !photo.key.endsWith('.mp4')).length;
  }

  public countVideos(photos: Array<any>): number {
    return photos.filter((photo) => photo.key.endsWith('.mp4')).length;
  }

  @Watch('albums')
  public onAlbumsStoreChange(val: Album[], oldVal: Album[]): void {
    logger.info('onAlbumsStoreChange', { val, oldVal });
    this.updateAlbum();
  }

  @Watch('photos')
  public onPhotoPropsChange(val: Photo[], oldVal: Photo[]): void {
    logger.info('onPhotoPropsChange', { val, oldVal });
    this.updateAlbum();
  }

  public onClick(albumId: string): void {
    logger.info('onClick', { albumId });
    if (albumId === 'slowmotion') {
      this.$router.push({ name: 'slowmotionview', params: { cameraid: this.camera.id, projectid: this.project.id } });
    } else {
      this.$router.push({ name: 'albumview', params: { id: albumId } });
    }
  }

  private updateAlbum() {
    this.items = this.albums.map((a) => ({ data: a }));
    if (this.slowMotions.length > 0) {
      this.items.push({
        data: {
          cameraId: this.camera.id,
          projectId: this.project.id,
          name: 'Video',
          id: 'slowmotion',
          photos: this.slowMotions,
        },
      });
    }
    this.items.sort((a, b) => a.data.name.localeCompare(b.data.name));
    this.$nextTick(() => {
      this.updatePhotosSize();
    });
  }

  public showCreateAlbumModal(): void {
    // Vérification si la référence 'createAlbumModal' est définie
    if (this.createAlbumModal && typeof this.createAlbumModal.show === 'function') {
      this.createAlbumModal.show();
    } else {
      console.error("La référence 'createAlbumModal' n'est pas disponible.");
    }
  }

  public async createAlbum(): Promise<void> {
    if (this.newAlbumName.trim() === '') {
      alert("Le nom de l'album ne peut pas être vide.");
      return;
    }

    try {
      // Créer un objet AlbumCreateRequest
      this.createAlbumRequest.name = this.newAlbumName;
      this.createAlbumRequest.projectId = this.project.id;
      this.createAlbumRequest.cameraId = this.camera.id;
      this.createAlbumRequest.photoId = ''; // Assurez-vous de remplir cela ou gérez-le comme optionnel si nécessaire

      // Log pour vérifier les données envoyées
      console.log("Données envoyées à l'API :", JSON.stringify(this.createAlbumRequest, null, 2));

      // Appel à l'API pour créer un nouvel album
      const albums = await this.createNewAlbum(this.createAlbumRequest);

      if (albums.length > 0) {
        this.$emit('updateAlbumList', albums); // Mettre à jour la liste des albums
      }

      this.newAlbumName = '';
      if (this.createAlbumModal && typeof this.createAlbumModal.hide === 'function') {
        this.createAlbumModal.hide();
      } else {
        console.error("La référence 'createAlbumModal' n'est pas disponible.");
      }
    } catch (error) {
      logger.error("Erreur lors de la création de l'album :", error);
      alert("Une erreur s'est produite lors de la création de l'album.");
    }
  }
}
